// Client for performance views built from run history. Backend: api/core/routers/performance.py.
// Account owners see their own data; admins can pass customerId. /admin/* endpoints are admin only.

import { ApiError, TokenGetter } from './learningApi';
import { HealthState, ModelHealth, DurationStats } from './reliabilityApi';

export type Severity = 'error' | 'warning' | 'info';

export interface FunctionWarning {
  code: 'p95_rising' | 'success_drop' | 'backup_share' | 'timeouts' | 'invalid_output' | 'near_platform_timeout';
  severity: Severity;
  label: string;
  detail: string;
}

/** Outcome counts and derived shares for one group of runs. */
export interface OutcomeStats {
  runs: number;
  ok: number;
  fallback: number;
  zdr_swap: number;
  timed_out: number;
  bad_output: number;
  usage_only: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  input_tokens: number;
  output_tokens: number;
  success_rate: number | null;
  backup_share: number | null;
  timeout_share: number | null;
  invalid_output_share: number | null;
  credits: number;
  credits_per_1k_runs: number | null;
}

export interface WhatIfRow {
  preset: 'relaxed' | 'balanced' | 'strict';
  percentile: string;
  multiplier: number;
  limit_s: number | null;
  note: string;
  runs_checked: number;
  would_cut_off: number | null;
  cut_off_share: number | null;
}

export interface LearningInfo {
  enabled: boolean;
  last_cycle_at: string | null;
  last_cycle_status: string | null;
  knowledge_published_at?: string | null;
  cycles?: { at: string; status: string; trigger: string; summary: Record<string, number | string> }[];
}

export interface FunctionPerformance {
  function: {
    id: string;
    name: string;
    version: number;
    is_public?: boolean;
    default_model: string | null;
    platform_tool: string | null;
    platform_timeout_s: number | null;
    platform_kind?: PlatformRouteRow['kind'];
    /** Admins only. */
    platform_builtin?: boolean;
    /** Admins only: set when an admin marked this function as a platform function. */
    platform_mark?: PlatformMarkSettings | null;
  };
  window: { days: number; unit: 'hour' | 'day'; since: string; until: string };
  summary: OutcomeStats | null;
  series: (OutcomeStats & { bucket: string; learned_limit_s: number | null })[];
  models: (OutcomeStats & { model_used: string | null })[];
  routes: { requested: string | null; selected: string | null; answered: string | null; upgraded?: boolean; runs: number; ok: number }[];
  errors: { label: string; runs: number }[];
  time_limit: {
    mode: string;
    preset: string;
    sample_size: number;
    current_limit_s: number | null;
    current_note: string;
    auto_limit_s: number | null;
    auto_note: string;
    stats: DurationStats;
    ceiling_s: number | null;
    min_samples: number;
  };
  what_if: WhatIfRow[];
  learning: LearningInfo | null;
}

/** Chain runs: `fallback` counts runs where at least one step was answered by a backup model. */
export interface ChainOutcomeStats {
  runs: number;
  ok: number;
  fallback: number;
  timed_out: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  credits: number;
  tokens: number;
  success_rate: number | null;
  backup_share: number | null;
  timeout_share: number | null;
  credits_per_1k_runs: number | null;
}

export interface ChainStep {
  node_id: string;
  label: string;
  kind: 'function' | 'tool' | 'removed';
  function_id: string | null;
  order: number;
  runs: number;
  ok: number;
  failed: number;
  fallback: number;
  upgraded: number;
  p50_ms: number | null;
  p95_ms: number | null;
  avg_ms: number | null;
  credits: number;
  model: string | null;
  success_rate: number | null;
  backup_share: number | null;
  share_of_run: number | null;
}

export interface ChainPerformance {
  chain: { id: string; name: string; steps: number };
  window: { days: number; unit: 'hour' | 'day'; since: string; until: string };
  summary: ChainOutcomeStats | null;
  series: (ChainOutcomeStats & { bucket: string; learned_limit_s: number | null })[];
  steps: ChainStep[];
  errors: { label: string; runs: number }[];
  time_limit: FunctionPerformance['time_limit'];
  what_if: WhatIfRow[];
}

export interface ChainListRow extends ChainOutcomeStats {
  chain_id: string;
  chain_name: string;
  customer_id: string;
  warnings: FunctionWarning[];
}

export interface ModelIncident {
  start: string;
  end: string;
  minutes: number;
  state: HealthState;
  reasons: string[];
  ongoing: boolean;
}

export interface ModelAttemptStats {
  model: string;
  calls: number;
  ok: number;
  timeouts?: number;
  rate_limited?: number;
  provider_errors?: number;
  bad_output?: number;
  other_failures?: number;
  skipped?: number;
  p50_s?: number | null;
  p95_s?: number | null;
}

export interface ModelReliability {
  window: { days: number; unit: 'hour' | 'day'; since: string; until: string; scope: 'mine' | 'all' };
  health_history_available: boolean;
  models: (ModelAttemptStats & {
    failure_rate?: number | null;
    live?: ModelHealth | null;
    incidents: ModelIncident[];
    affected_functions: { function_name: string; runs_24h: number }[];
  })[];
  series: (ModelAttemptStats & { bucket: string })[];
}

export type UsageRow = OutcomeStats & { function_name?: string; model_used?: string | null };

export interface UsageReport {
  window: { days: number; since: string; until: string };
  group_by: 'function' | 'model';
  rows: UsageRow[];
}

export interface MonthlySummary {
  month: string;
  since: string;
  until: string;
  complete: boolean;
  totals: OutcomeStats | null;
  functions: UsageRow[];
  daily: (OutcomeStats & { bucket: string })[];
}

export interface PlatformRouteRow extends OutcomeStats {
  customer_id: string;
  customer_tag: string | null;
  function_name: string;
  function_id: string | null;
  platform_tool: string | null;
  kind: 'text' | 'search' | 'vision' | 'kitchen' | 'other' | 'regional_search' | null;
  default_model: string | null;
  runs_per_min: number;
  runs_last_hour: number;
  platform_timeout_s: number | null;
  near_timeout: number | null;
  near_timeout_share: number | null;
  time_limit_s: number | null;
  time_limit_note: string | null;
  headroom_s: number | null;
  learning: LearningInfo | null;
  daily: { day: string; runs: number; p95_ms: number | null }[];
  warnings: FunctionWarning[];
  /** Marked as a platform function by an admin, rather than built in. */
  custom?: boolean;
  mark?: (PlatformMarkSettings & { created_by: string | null; created_at: string | null }) | null;
}

export type PlatformKind = 'text' | 'vision' | 'search' | 'kitchen' | 'other';

export interface PlatformMarkSettings {
  tool: string;
  go_timeout_s: number | null;
  kind: PlatformKind;
  /** Set when the function is served as a dynamic route at /napi/tools/{slug}. */
  slug?: string | null;
  status?: 'off' | 'testing' | 'live';
}

export interface PlatformFunctionMark extends PlatformMarkSettings {
  customer_id: string;
  customer_tag: string | null;
  original_uuid: string;
  function_id: string | null;
  function_name: string | null;
  built_in: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PlatformRoutes {
  window: { days: number; since: string; until: string };
  platform_customer: { customer_id: string; customer_tag: string | null } | null;
  candidates: { customer_id: string; customer_tag: string | null; functions: number }[];
  near_timeout_ratio: number;
  routes: PlatformRouteRow[];
}

export interface AdminAlert {
  kind: 'model' | 'model_incident' | 'function' | 'platform_route' | 'chain';
  severity: Severity;
  title: string;
  detail: string;
  model?: string;
  models?: string[];
  customer_id?: string;
  customer_tag?: string | null;
  function_name?: string;
  function_id?: string | null;
  chain_id?: string;
  runs?: number;
  warnings?: FunctionWarning[];
}

export interface AdminAlerts {
  generated_at: string;
  health_history_available: boolean;
  alerts: AdminAlert[];
}

type Params = Record<string, string | number | null | undefined>;

function query(params: Params): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function performanceApi(getToken: TokenGetter) {
  async function send(path: string, init?: { method: 'PUT' | 'DELETE'; body?: unknown }): Promise<Response> {
    const token = await getToken();
    const res = await fetch(`/api/v2/performance${path}`, {
      method: init?.method,
      headers: { Authorization: `Bearer ${token}`, ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res;
  }

  const get = async <T,>(path: string): Promise<T> => (await send(path)).json();

  /** Fetches a CSV with auth and saves it through a temporary link. */
  async function download(path: string, fallbackName: string): Promise<void> {
    const res = await send(path);
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1] || fallbackName;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return {
    function: (id: string, days: number, customerId?: string | null) =>
      get<FunctionPerformance>(`/functions/${id}${query({ days, customer_id: customerId })}`),
    chain: (id: string, days: number, customerId?: string | null) =>
      get<ChainPerformance>(`/chains/${id}${query({ days, customer_id: customerId })}`),
    chains: (customerId?: string | null) =>
      get<{ window_days: number; chains: ChainListRow[] }>(`/chains${query({ customer_id: customerId })}`),
    warnings: (customerId?: string | null) =>
      get<{ window_days: number; functions: Record<string, FunctionWarning[]> }>(`/warnings${query({ customer_id: customerId })}`),
    models: (days: number, scope: 'mine' | 'all', customerId?: string | null) =>
      get<ModelReliability>(`/models${query({ days, scope, customer_id: customerId })}`),
    usage: (days: number, groupBy: 'function' | 'model', customerId?: string | null) =>
      get<UsageReport>(`/usage${query({ days, group_by: groupBy, customer_id: customerId })}`),
    usageCsv: (days: number, groupBy: 'function' | 'model', customerId?: string | null) =>
      download(`/usage${query({ days, group_by: groupBy, format: 'csv', customer_id: customerId })}`, `usage-${groupBy}-${days}d.csv`),
    monthly: (month: string, customerId?: string | null) =>
      get<MonthlySummary>(`/usage/monthly${query({ month, customer_id: customerId })}`),
    monthlyCsv: (month: string, customerId?: string | null) =>
      download(`/usage/monthly${query({ month, format: 'csv', customer_id: customerId })}`, `monthly-summary-${month}.csv`),
    platformRoutes: (days: number, customerId?: string | null) =>
      get<PlatformRoutes>(`/admin/platform-routes${query({ days, customer_id: customerId })}`),
    alerts: () => get<AdminAlerts>('/admin/alerts'),
    platformFunctions: () => get<{ kinds: PlatformKind[]; functions: PlatformFunctionMark[] }>('/admin/platform-functions'),
    markPlatformFunction: async (functionId: string, settings: PlatformMarkSettings): Promise<PlatformFunctionMark> =>
      (await send(`/admin/platform-functions/${functionId}`, { method: 'PUT', body: settings })).json(),
    unmarkPlatformFunction: async (functionId: string): Promise<void> => {
      await send(`/admin/platform-functions/${functionId}`, { method: 'DELETE' });
    },
  };
}
